import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '@/locales/strings';
import { useModalA11y } from '@/hooks/useModalA11y';
import { track } from '@/utils/analytics';
import {
  CalendarIcon as NavCalendarIcon,
  BookIcon as NavBookIcon,
  PersonIcon as NavPersonIcon,
  BodyIcon as NavBodyIcon,
} from '@/components/training/BottomNav';

/**
 * AppGuide — a re-readable bottom sheet listing every surface of the
 * app with a one-line description. Triggered from the `?` button in
 * the WeekHome header. Unlike FirstRunTour it is opt-in, dismissable,
 * and re-openable, so it carries the long-form orientation that
 * doesn't belong in a forced first-run modal.
 *
 * Each row links to its destination so the guide doubles as a
 * navigation index for users who haven't memorised the bottom nav.
 */
export default function AppGuide({
  open,
  onClose,
  onReplayTour,
  onOpenWhatsNew,
  hasUnreadWhatsNew = false,
  lang = 'en',
}) {
  const dialogRef = useModalA11y({ open, onClose });
  const navigate = useNavigate();

  useEffect(() => {
    if (open) track('app_guide_opened');
  }, [open]);

  if (!open) return null;

  // Bottom-nav glyphs (HouseIcon / CalendarIcon / BookIcon /
  // PersonIcon) are reused for the destinations that exist in the
  // tab bar so a user learns one icon language across surfaces.
  // Today / player / streak / method don't have nav twins and use
  // bespoke glyphs defined below.
  const sections = [
    { key: 'today',    icon: TodayIcon,                        to: '/home' },
    { key: 'anatomy',  icon: AnatomyIcon,                      to: '/anatomy' },
    { key: 'player',   icon: PlayerIcon,                       to: null },
    { key: 'streak',   icon: StreakIcon,                       to: '/progress' },
    { key: 'method',   icon: MethodIcon,                       to: '/method' },
    // Body tab — added with the IA migration step 6 (5-tab nav).
    // Keeps Calendar in this list because /calendar is still a real
    // route reachable by deep-link / reminder URL even though it
    // dropped from the bottom bar.
    { key: 'body',     icon: () => navIcon(NavBodyIcon),       to: '/body' },
    { key: 'calendar', icon: () => navIcon(NavCalendarIcon),   to: '/calendar' },
    { key: 'learning', icon: () => navIcon(NavBookIcon),       to: '/learning' },
    { key: 'profile',  icon: () => navIcon(NavPersonIcon),     to: '/profile' },
  ];

  // Adapter — BottomNav icons take { color, active } and render at
  // 22×22 with stroke 1.5; the AppGuide pill is 40×40 with the
  // primary-pale background and primary-dark glyph color, so we
  // pass the active style to get the filled/lit variant.
  function navIcon(NavIcon) {
    return <NavIcon color="currentColor" active />;
  }

  const go = (path, key) => {
    if (!path) return;
    track('app_guide_section_tapped', { section: key });
    onClose();
    navigate(path);
  };

  const close = () => {
    track('app_guide_closed');
    onClose();
  };

  const replayTour = () => {
    track('app_guide_replay_tour_tapped');
    onClose();
    if (typeof onReplayTour === 'function') onReplayTour();
  };

  const openWhatsNew = () => {
    track('app_guide_whats_new_tapped');
    onClose();
    if (typeof onOpenWhatsNew === 'function') onOpenWhatsNew();
  };

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('app_guide_title', lang)}
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 700,
        background: 'rgba(28, 14, 22, 0.58)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'bb-guide-fade 240ms ease-out',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          maxHeight: '88dvh',
          background:
            'linear-gradient(180deg, #fff 0%, var(--primary-pale, #fbeae7) 140%)',
          borderTopLeftRadius: 26,
          borderTopRightRadius: 26,
          paddingTop: 18,
          paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
          paddingLeft: 20,
          paddingRight: 20,
          boxShadow: '0 -30px 80px -20px rgba(45,31,38,.45)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'bb-guide-slide 320ms cubic-bezier(0.32,0.72,0,1)',
        }}
      >
        {/* Drag handle */}
        <div aria-hidden="true" style={{
          width: 44, height: 4, borderRadius: 999,
          background: 'color-mix(in srgb, var(--ink, #2d1f26) 14%, transparent)',
          margin: '0 auto 14px',
        }} />

        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{
            font: '500 22px/1.2 var(--font-title, Fraunces), serif',
            color: 'var(--ink, #2d1f26)',
            letterSpacing: '-0.005em',
          }}>
            {t('app_guide_title', lang)}
          </div>
          <div style={{
            font: '400 13px/1.45 var(--font-body, Manrope)',
            color: 'var(--ink-dim, #6b5560)',
            maxWidth: 320,
            margin: '6px auto 0',
            textWrap: 'balance',
          }}>
            {t('app_guide_subtitle', lang)}
          </div>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            paddingBottom: 12,
          }}
        >
          {/* Replay row — sits above the section list so a user who
              wants the visual lesson again can find it without
              scrolling. Only rendered when WeekHome has wired
              onReplayTour (it always does today). */}
          {typeof onReplayTour === 'function' && (
            <button
              type="button"
              onClick={replayTour}
              aria-label={t('app_guide_replay_title', lang)}
              style={{
                display: 'flex',
                gap: 14,
                alignItems: 'flex-start',
                textAlign: 'left',
                padding: '14px 14px',
                borderRadius: 18,
                background:
                  'linear-gradient(180deg, var(--primary-pale, #fbeae7) 0%, #fff 110%)',
                border: '1px solid color-mix(in srgb, var(--primary, #c97a68) 28%, transparent)',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                width: '100%',
                font: 'inherit',
                color: 'inherit',
              }}
            >
              <span aria-hidden="true" style={{
                flexShrink: 0,
                width: 40, height: 40,
                borderRadius: 12,
                background: 'var(--primary, #c97a68)',
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 1 0 3-6.7" />
                  <path d="M3 4v5h5" />
                </svg>
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  font: '500 15px/1.25 var(--font-title, Fraunces), serif',
                  color: 'var(--ink, #2d1f26)',
                  marginBottom: 2,
                }}>
                  {t('app_guide_replay_title', lang)}
                </div>
                <div style={{
                  font: '400 13px/1.45 var(--font-body, Manrope)',
                  color: 'var(--ink-dim, #6b5560)',
                }}>
                  {t('app_guide_replay_body', lang)}
                </div>
              </span>
              <span aria-hidden="true" style={{
                flexShrink: 0,
                alignSelf: 'center',
                color: 'color-mix(in srgb, var(--primary, #c97a68) 60%, transparent)',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </span>
            </button>
          )}

          {/* What's new row — opens the sheet without the user
              having to wait for a banner. Sits below the replay
              row so the two utility actions are grouped at the top
              of the guide. Renders only when WeekHome bridges
              onOpenWhatsNew (it always does today). The unread dot
              appears only when there is a fresh entry the user has
              not yet acknowledged. */}
          {typeof onOpenWhatsNew === 'function' && (
            <button
              type="button"
              onClick={openWhatsNew}
              aria-label={t('app_guide_whats_new_title', lang)}
              style={{
                display: 'flex',
                gap: 14,
                alignItems: 'flex-start',
                textAlign: 'left',
                padding: '14px 14px',
                borderRadius: 18,
                background: '#fff',
                border: '1px solid color-mix(in srgb, var(--primary, #c97a68) 14%, transparent)',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                width: '100%',
                font: 'inherit',
                color: 'inherit',
              }}
            >
              <span aria-hidden="true" style={{
                position: 'relative',
                flexShrink: 0,
                width: 40, height: 40,
                borderRadius: 12,
                background: 'var(--primary-pale, #fbeae7)',
                color: 'var(--primary-dark, #a85c4b)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 4h11l3 3v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
                  <path d="M16 4v3h3" />
                  <path d="M8 12h8M8 16h6" />
                </svg>
                {hasUnreadWhatsNew && (
                  <span style={{
                    position: 'absolute',
                    top: -2, right: -2,
                    width: 10, height: 10,
                    borderRadius: 999,
                    background: 'var(--primary, #c97a68)',
                    boxShadow: '0 0 0 2px #fff',
                  }} />
                )}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  font: '500 15px/1.25 var(--font-title, Fraunces), serif',
                  color: 'var(--ink, #2d1f26)',
                  marginBottom: 2,
                }}>
                  {t('app_guide_whats_new_title', lang)}
                </div>
                <div style={{
                  font: '400 13px/1.45 var(--font-body, Manrope)',
                  color: 'var(--ink-dim, #6b5560)',
                }}>
                  {t('app_guide_whats_new_body', lang)}
                </div>
              </span>
              <span aria-hidden="true" style={{
                flexShrink: 0,
                alignSelf: 'center',
                color: 'color-mix(in srgb, var(--primary, #c97a68) 60%, transparent)',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </span>
            </button>
          )}

          {sections.map(({ key, icon: Icon, to }) => {
            const interactive = !!to;
            const Tag = interactive ? 'button' : 'div';
            return (
              <Tag
                key={key}
                {...(interactive ? {
                  type: 'button',
                  onClick: () => go(to, key),
                  'aria-label': t(`app_guide_section_${key}_title`, lang),
                } : {})}
                style={{
                  display: 'flex',
                  gap: 14,
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  padding: '14px 14px',
                  borderRadius: 18,
                  background: '#fff',
                  border: '1px solid color-mix(in srgb, var(--primary, #c97a68) 14%, transparent)',
                  boxShadow: '0 6px 18px -14px rgba(45,31,38,0.35)',
                  cursor: interactive ? 'pointer' : 'default',
                  WebkitTapHighlightColor: 'transparent',
                  width: '100%',
                  font: 'inherit',
                  color: 'inherit',
                }}
              >
                <span aria-hidden="true" style={{
                  flexShrink: 0,
                  width: 40, height: 40,
                  borderRadius: 12,
                  background: 'var(--primary-pale, #fbeae7)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--primary-dark, #a85c4b)',
                }}>
                  <Icon />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    font: '500 15px/1.25 var(--font-title, Fraunces), serif',
                    color: 'var(--ink, #2d1f26)',
                    marginBottom: 2,
                  }}>
                    {t(`app_guide_section_${key}_title`, lang)}
                  </div>
                  <div style={{
                    font: '400 13px/1.45 var(--font-body, Manrope)',
                    color: 'var(--ink-dim, #6b5560)',
                  }}>
                    {t(`app_guide_section_${key}_body`, lang)}
                  </div>
                </span>
                {interactive && (
                  <span aria-hidden="true" style={{
                    flexShrink: 0,
                    alignSelf: 'center',
                    color: 'color-mix(in srgb, var(--primary, #c97a68) 60%, transparent)',
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </span>
                )}
              </Tag>
            );
          })}
        </div>

        <button
          type="button"
          onClick={close}
          style={{
            marginTop: 4,
            width: '100%',
            minHeight: 46,
            borderRadius: 999,
            border: '1px solid color-mix(in srgb, var(--ink, #2d1f26) 10%, transparent)',
            background: 'transparent',
            color: 'var(--ink-dim, #6b5560)',
            font: '600 13px/1 var(--font-ui, Manrope)',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t('app_guide_close', lang)}
        </button>
      </div>

      <style>{`
        @keyframes bb-guide-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes bb-guide-slide {
          from { transform: translateY(20px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}

const ICON_BASE = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

function TodayIcon() {
  return (
    <svg {...ICON_BASE}>
      <circle cx="12" cy="12" r="9" />
      <path d="M10 9l5 3-5 3z" fill="currentColor" stroke="none" />
    </svg>
  );
}
function PlayerIcon() {
  return (
    <svg {...ICON_BASE}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function AnatomyIcon() {
  return (
    <svg {...ICON_BASE}>
      {/* Stylised pelvic-floor silhouette: torso outline + three
          short concentric arcs at the floor area. Mirrors the
          anatomy diagram on /anatomy. */}
      <path d="M 9 3 Q 12 1.5 15 3 L 16 8 Q 17 12 16 16 L 17 21 L 7 21 L 8 16 Q 7 12 8 8 Z" />
      <path d="M 9 17 q 3 2 6 0" />
      <path d="M 10 18 q 2 1 4 0" />
    </svg>
  );
}
function StreakIcon() {
  return (
    <svg {...ICON_BASE}>
      <path d="M12 3c1 3 4 5 4 9a4 4 0 0 1-8 0c0-2 1-3 1-5 1 1 2 1 3-4z" />
    </svg>
  );
}
function MethodIcon() {
  return (
    <svg {...ICON_BASE}>
      <path d="M4 18c4-8 12-8 16 0" />
      <circle cx="6" cy="14" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="9" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="18" cy="14" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}
// CalendarIcon, LearningIcon, ProfileIcon used to live here as
// hand-redrawn versions of the bottom-nav glyphs; they were
// retired in favour of importing the originals from BottomNav.jsx
// so the AppGuide and the tab bar speak the same icon language.
